<template>
  <div>
    <v-data-table
      v-model="selected"
      @dblclick:row="showInvoiceDetails"
      @toggle-select-all="selectAllToggle"
      :no-data-text="$t('table.noResults')"
      :items-per-page="itemsPerPage"
      :headers="approvedHeaders"
      :loading="isLoading"
      :items="adminInvoices"
      :show-select="canPayInvoices"
      hide-default-footer
      disable-sort
      show-expand
    >
      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          @input="select($event)"
          :disabled="disableSelect(item)"
          :readonly="disableSelect(item)"
          :value="isSelected"
          class="select-checkbox"
        />
      </template>
      <template v-slot:item.created_at="{ item }">
        {{ date(item.created_at) }}
      </template>
      <template v-slot:item.invoice_file_analysis="{ item }">
        <InvoiceAutomaticValidationIcons :invoice="item" />
      </template>
      <template v-slot:item.transfer="{ item }">
        <v-chip :color="handleColors(rawTransferStatus(item.transfer))" small>
          {{ transferStatus(item.transfer) }}
        </v-chip>
      </template>
      <template v-slot:item.total="{ item }">
        {{ formatMoney(item.total) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <AdminInvoiceTableMenu @retryTransfer="retryTransfer" :invoice="item" />
      </template>
      <template v-slot:[`expanded-item`]="{ headers, item }">
        <td :colspan="headers.length" class="content pl-13 pb-10">
          <v-card class="rounded-b-lg" outlined>
            <PaymentList :payments="item.payment_list" />
          </v-card>
        </td>
      </template>
    </v-data-table>

    <v-divider />

    <h-pagination
      :fetch="fetchAdminInvoices"
      :requireOrderByFilter="false"
      :noBorders="true"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { approvedHeaders, invoiceHeaders } from '../adminInvoiceHeaders'
import { handleColors } from '@/util/colors'
import { adminInvoicesZip } from '../adminInvoiceService'
import { userHasSomeRole } from '@/util/helpers'

const Status = Object.freeze({
  APPROVED: 'APPROVED',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED'
})

const Sender = Object.freeze({
  HOST: 'HOST',
  GUEST: 'GUEST'
})

export default {
  name: 'AdminApprovedInvoices',

  components: {
    PaymentList: () => import('./fragments/PaymentList'),
    AdminInvoiceTableMenu: () => import('./fragments/AdminInvoiceTableMenu'),
    InvoiceAutomaticValidationIcons: () => import('./fragments/InvoiceAutomaticValidationIcons')
  },

  watch: {
    selected() {
      this.$emit('selectedInvoices', this.selected)
    }
  },

  computed: {
    ...mapGetters(['adminInvoices', 'isLoading', 'itemsPerPage', 'transfer', 'filters']),

    canPayInvoices() {
      return !userHasSomeRole(['ROLE_PARTNER'])
    }
  },

  methods: {
    ...mapActions(['fetchAdminInvoices', 'showErrorMessage']),

    date(date) {
      return date ? catalogDate(date) : '---'
    },

    description(description) {
      return description ?? '---'
    },

    disableSelect(item) {
      return !!item.transfer?.status
    },

    selectAllToggle({ items }) {
      if (this.selected.length >= 1) {
        this.selected = []
        return
      }

      this.selected = []
      const self = this

      items.forEach(item => {
        if (!this.disableSelect(item)) {
          self.selected.push(item)
        }
      })
    },

    rawTransferStatus(transfer) {
      if (!transfer) return 'PROCESSING'
      return transfer?.status
    },

    transferStatus(transfer) {
      if (!transfer) return this.$t('hausefulInvoices.waitingTransfer')
      return this.$t(`transfer.transferStatus.${transfer.status}`)
    },

    showInvoiceDetails(_, { item }) {
      if (!item || !item?.id) {
        this.showErrorMessage('hausefulInvoices.invoiceNotFound')
        return
      }
      this.$emit('invoice', item)
    },

    retryTransfer(invoice) {
      this.$emit('retryTransfer', invoice)
    },

    async downloadInvoicesZip() {
      this.loading = true
      try {
        const filterParams = {
          size: this.filters.size || 100,
          page: this.filters.page || 0,
          hasTransfer: this.filters.hasTransfer !== undefined ? this.filters.hasTransfer : false,
          status: this.filters.status || 'APPROVED',
          sender: this.filters.sender || 'HOST',
          ...this.filters
        }

        const response = await adminInvoicesZip(filterParams);

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'invoices_pending_export.zip')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.showErrorMessage(this.$t('auth.errors.errorGeneratingPdf'))
      } finally {
        this.loading = false
      }
    }
  },

  data() {
    return {
      handleColors,
      approvedHeaders,
      invoiceHeaders,
      formatMoney: catalogMonetary,
      selected: [],
      loading: false
    }
  }
}
</script>

<style>
.select-checkbox {
  transform: scale(0.75);
}
</style>